import { useMutation } from '@tanstack/react-query'
import { useContext } from 'react'
import { useLocation } from 'react-router-dom'
import { baseClient } from 'builder/modules/apiClient'
import { useI18n } from 'builder/hooks/useI18n'
import { SnackBarContext, SnackBarTypes } from 'builder/components/SnackBarV2'
import { useFeaturesConfig } from '../../../hooks/featureConfig/useFeaturesConfig'
import { TRANSLATION } from '../utils'
import { usePostRedirectUrl } from './useGetRedirectUrl'

export function useSaveCoursesMutation(props: { onError: () => void }) {
  const { i18n } = useI18n()
  const { pathname } = useLocation()
  const { mutateAsync } = usePostRedirectUrl()
  const { features } = useFeaturesConfig()
  const { openSnackBar } = useContext(SnackBarContext)
  const desktopBottom = features?.aiCoach && pathname === '/' ? 96 : 16

  return useMutation({
    mutationKey: ['save-course'],
    mutationFn: async (courseData: { CMSGID: string; isSaved: boolean }) => {
      const endpoint = `/learning/courses/${courseData.CMSGID}/save`
      return await baseClient.post(endpoint, {
        id: courseData.CMSGID,
      })
    },

    onSuccess: (_, variables) => {
      openSnackBar({
        text: variables.isSaved
          ? i18n.t(`${TRANSLATION}.actions.saved`)
          : i18n.t(`${TRANSLATION}.actions.removed`),
        action: {
          text: 'View',
          onClick: async () => {
            try {
              const response = await mutateAsync({ cmsgid: 'saved' })
              const url = new URL(response.redirect_url)
              url.pathname = 'my-library/'
              const finalUrl = url.toString() + '&tab=saved'
              window.open(finalUrl, '_blank')
            } catch (error) {
              // No-op since the error is handled in the usePostRedirectUrl hook
              console.error('Error redirecting to saved courses:', error)
            }
          },
        },
        type: SnackBarTypes.success,
        bottom: { phone: 96, desktop: desktopBottom },
      })
    },
    onError: (error: Error) => {
      console.error('Error saving course:', error.message)
      if (props.onError) {
        props.onError()
      }
      openSnackBar({
        text: i18n.t(`${TRANSLATION}.errors.generic`),
        type: SnackBarTypes.failure,
        bottom: { phone: 96, desktop: desktopBottom },
      })
    },
  })
}
